/**
 * Boards over MCP — deletion. Beside `boardWrites.js`, behind the destructive
 * switch, and the most dangerous three tools in the feature.
 *
 * ## Every delete is a tombstone and there is no undo
 *
 * `extension_records_delete` sets `deleted = 1`, bumps the version and queues
 * one outbox `delete` op. It reaches every device the user owns and, in a team
 * scope, every teammate. Nothing in this app un-deletes a record; the documents
 * side says so in its own words (`deletedDocsGuard.js:24-25`). A card deleted
 * here is gone, with its comments — they live INSIDE the task record.
 *
 * ## Why a board delete is N transactions and not one, stated as a cost
 *
 * The app's own `boardsStore.deleteBoard` is a single `extension_records_sync_scope`
 * with an empty record array, which tombstones the whole scope in ONE
 * transaction. It is the right primitive and it is **forbidden here**: that same
 * command, handed a stale array, tombstones every record absent from it, and a
 * test pins that no file under `src/mcp/` may even name it. Giving the MCP
 * surface that primitive to gain atomicity on the rarest operation in the set
 * would be trading a real, verified data-loss path for a tidier failure mode.
 *
 * So a board delete is one `extension_records_delete` per record: N
 * transactions, N outbox ops, **not atomic**. A failure part-way leaves a
 * partially deleted board. That is reported honestly — a partial result is an
 * error carrying `deleted` / `failed` / `complete: false`, never a cheerful
 * success — and the delete order is chosen so that a partial failure fails
 * SAFE: children first, the board record last.
 *
 * ## Children before the parent, for both cascading deletes
 *
 * Stop half-way through the other order and you have orphans: tasks whose
 * `parent_id` names a list that no longer exists, or records whose board head
 * is gone. `assembleBoard` returns null without a board record, so those rows
 * are present, synced, counting against the scope's row cap, and reachable from
 * nothing. Stop half-way through THIS order and the user sees a board with
 * fewer cards — visible, and deletable again.
 *
 * ## `markDocsDeleted` before the first tombstone
 *
 * The app's own `handleDeleteTask` calls it first and says why: an image still
 * uploading for that task would otherwise finish and leave ciphertext in the
 * bucket that nothing references. The same is true for a task an agent deletes,
 * so the same mark is taken, synchronously, before the delete is issued.
 *
 * ## The open board tab closes itself
 *
 * After a board delete the nonce bump drives `reconcileFromDisk`, whose
 * `readBoard` returns null and which then calls `closeTaskManagerBoardTab`.
 * That is the component's own code doing it, from the component. MCP never
 * names a tab primitive, and a test forbids it from doing so.
 */
import { invoke } from '@tauri-apps/api/core';
import { markDocsDeleted } from '../../extensions/bundled/documents/deletedDocsGuard';
import { PERSONAL } from '../scopes';
import { mcpError } from '../errors';
import { stageBoardRecords } from '../boardChanges';
import { scopeReport } from '../writeScope';
import { targetScope, boardRows, findBoard, findGroup, findTask } from './boardWrites';

const EXTENSION_ID = 'task-manager';

/** The wire shape `patchBoardWithRecords`'s delete branch looks up by id. */
const deletionRecord = (id) => ({ id, deleted: true });

/** One tombstone. `true` on success, `false` on any store failure. */
async function tombstone(scopeKey, id) {
  try {
    await invoke('extension_records_delete', {
      workspaceId: scopeKey,
      extensionId: EXTENSION_ID,
      recordId: String(id),
    });
    return true;
  } catch {
    // The underlying text is never relayed (see `mcp/errors.js`).
    return false;
  }
}

/**
 * Tombstone a list of ids IN THE GIVEN ORDER, stopping at the first failure.
 * The order is the caller's safety property, so this must not reorder or
 * parallelise: `Promise.all` would delete a parent before its children on any
 * scheduling that happened to favour it.
 */
async function tombstoneInOrder(scopeKey, ids) {
  const deleted = [];
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    const gone = await tombstone(scopeKey, id);
    if (!gone) return { deleted, failed: ids.slice(deleted.length) };
    deleted.push(id);
  }
  return { deleted, failed: [] };
}

/**
 * A partial delete DID change stored records, so its records are staged on the
 * error too — `runHandler` refreshes the open UI for a staged result even when
 * the call failed. Leaving them out would leave the open tab showing cards that
 * no longer exist, and the tab's next whole-board persist would then RESURRECT
 * them (its snapshot still contains them, and `sync_scope` upserts everything
 * in the array it is given).
 */
function partialFailure(scopeKey, wire, extra, deleted) {
  const err = mcpError(
    'write_failed',
    'The delete stopped part-way. Some of those records are gone and the rest are still there; read the board again before retrying.',
    { ...extra, complete: false, scope: wire, ...scopeReport(wire) }
  );
  return stageBoardRecords(err, scopeKey, deleted.map(deletionRecord));
}

/** Delete one card. Its comments go with it — they are inside the record. */
export async function boardTaskDelete({ board_id: boardId, task_id: taskId, scope }) {
  const wire = scope || PERSONAL;
  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');
  const taskRow = findTask(rows, taskId);
  if (!taskRow) return mcpError('not_found', 'No task with that id on this board.');

  // BEFORE the delete is issued, not after: an image still uploading for this
  // task would otherwise finish and leave ciphertext nothing references.
  markDocsDeleted([taskRow.id]);
  const gone = await tombstone(target.scopeKey, taskRow.id);
  if (!gone) {
    return mcpError('write_failed', 'That task could not be deleted, and nothing was changed.', {
      scope: wire,
    });
  }

  const result = {
    boardId: boardRow.id,
    id: taskRow.id,
    deleted: [taskRow.id],
    count: 1,
    complete: true,
    scope: wire,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, target.scopeKey, [deletionRecord(taskRow.id)]);
}

/**
 * Delete one list. Its cards have `parent_id = list_id`, so deleting only the
 * list would leave them placed in a column that does not exist — present,
 * synced, and rendered in no column at all. `cascade: true` is therefore
 * required whenever the list holds any card.
 */
export async function boardListDelete({ board_id: boardId, list_id: listId, scope, cascade }) {
  const wire = scope || PERSONAL;
  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');
  const groupRow = findGroup(rows, listId);
  if (!groupRow) return mcpError('not_found', 'No list with that id on this board.');
  if (groupRow.parent_id !== boardRow.id) {
    return mcpError('invalid_parent', 'That list belongs to a different board.', {
      boardId: boardRow.id,
      listId: groupRow.id,
    });
  }

  const tasks = rows.filter((r) => r.record_type === 'task' && r.parent_id === groupRow.id);
  if (tasks.length && cascade !== true) {
    return mcpError(
      'cascade_required',
      'That list still holds cards, and deleting it deletes them permanently — there is no undo. Nothing was deleted. Call again with cascade: true, or move the cards first with board_task_move.',
      {
        boardId: boardRow.id,
        listId: groupRow.id,
        count: tasks.length,
        tasks: tasks.map((t) => ({ id: t.id, title: t.title ?? null })),
        scope: wire,
        ...scopeReport(wire),
      }
    );
  }

  const taskIds = tasks.map((t) => t.id);
  markDocsDeleted(taskIds);
  // Cards first, then the column. The other order orphans them.
  const { deleted, failed } = await tombstoneInOrder(target.scopeKey, [...taskIds, groupRow.id]);
  if (failed.length) {
    return partialFailure(
      target.scopeKey,
      wire,
      { boardId: boardRow.id, listId: groupRow.id, deleted, failed },
      deleted
    );
  }

  const result = {
    boardId: boardRow.id,
    id: groupRow.id,
    deleted,
    count: deleted.length,
    tasksDeleted: taskIds.length,
    complete: true,
    scope: wire,
    ...scopeReport(wire),
  };
  return stageBoardRecords(result, target.scopeKey, deleted.map(deletionRecord));
}

/**
 * Delete a whole board: every list, every card, then the board itself.
 *
 * `cascade: true` is required ALWAYS, even for an empty board — a board delete
 * is never a single record from the user's point of view, and the one-node
 * exemption that makes sense for a childless document does not carry over to a
 * whole board with a name the user chose.
 */
export async function boardDelete({ board_id: boardId, scope, cascade }) {
  const wire = scope || PERSONAL;
  const target = targetScope(wire);
  if (target.error) return target.error;

  const rows = await boardRows(target.scopeKey, boardId);
  const boardRow = findBoard(rows, boardId);
  if (!boardRow) return mcpError('not_found', 'No board with that id in this scope.');

  const groups = rows.filter((r) => r.record_type === 'group');
  const tasks = rows.filter((r) => r.record_type === 'task');

  if (cascade !== true) {
    return mcpError(
      'cascade_required',
      'Deleting a board deletes the board, every list on it and every card in them, permanently — there is no undo, and it disappears on the user\'s other devices too. Nothing was deleted. Call again with cascade: true if that is what you want.',
      {
        boardId: boardRow.id,
        name: boardRow.name ?? null,
        lists: groups.length,
        tasks: tasks.length,
        count: rows.length,
        scope: wire,
        ...scopeReport(wire),
      }
    );
  }

  markDocsDeleted(tasks.map((t) => t.id));
  // Cards, then lists, then the board record LAST — so a failure part-way
  // leaves a reachable board with fewer cards, never orphans under a board
  // that no longer exists. `rows` may also carry records of a type this
  // version does not know about; those go with the children, before the head.
  const others = rows.filter(
    (r) => r.id !== boardRow.id && r.record_type !== 'task' && r.record_type !== 'group'
  );
  const order = [
    ...tasks.map((t) => t.id),
    ...groups.map((g) => g.id),
    ...others.map((r) => r.id),
    boardRow.id,
  ];
  const { deleted, failed } = await tombstoneInOrder(target.scopeKey, order);
  if (failed.length) {
    return partialFailure(
      target.scopeKey,
      wire,
      { boardId: boardRow.id, deleted, failed },
      deleted
    );
  }

  const result = {
    id: boardRow.id,
    name: boardRow.name ?? null,
    deleted,
    count: deleted.length,
    listsDeleted: groups.length,
    tasksDeleted: tasks.length,
    complete: true,
    scope: wire,
    ...scopeReport(wire),
  };
  // The board record itself is not paintable — `patchBoardWithRecords` ignores
  // board-level records by design. The open tab closes through the nonce's
  // disk re-read instead (`readBoard` -> null -> the tab closes itself).
  return stageBoardRecords(
    result,
    target.scopeKey,
    deleted.filter((id) => id !== boardRow.id).map(deletionRecord)
  );
}
